// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'
import type { Size, Status } from '../shared'

/**
 * `<qds-form-message>` elements represent messages for a form field in a user
 * interface.
 *
 * @see https://quartz.se.com/build/components/form-message
 */
@Component({
  tag: 'qds-form-message',
  shadow: true,
  styleUrl: 'form-message.css',
})
export class FormMessage implements ComponentInterface {
  /**
   * Adds vertical margin to the form message for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * The form message's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The form message's status.
   */
  @Prop() public readonly status?: Status = 'error'

  /**
   * Adds accessible text to the icon's form message that will be used by screen readers.
   *
   * @example
   * <qds-form-message status-description="Error" text="Password is required."></qds-form-message>
   * // This form message will be read by screen readers as "Error Password is required."
   */
  @Prop() public readonly statusDescription?: string

  /**
   * The form message's text.
   */
  @Prop() public readonly text?: string

  @Element() private readonly host!: HTMLQdsFormMessageElement

  #inheritedAttributes: Attributes = {}

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedStatus(): Status {
    return this.status ?? 'error'
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <span
        class={{
          'qds-form-message': true,
          'qds-inline': this.inline,
        }}
        data-size={this.#computedSize}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        <div class="qds-icon-container" data-size={this.#computedSize}>
          <div
            class="qds-icon-background"
            data-size={this.#computedSize}
            data-status={this.status}
          >
            <qds-icon
              aria-hidden="true"
              class="qds-icon"
              data-size={this.#computedSize}
              name={`status-${this.status}`}
              library="core"
            />
            <span class="qds-sr-only">{this.statusDescription}</span>
          </div>
        </div>
        <span class="qds-text" data-status={this.#computedStatus}>
          {this.text}
        </span>
      </span>
    )
  }
}
